import { useState, useEffect } from 'react';
import { useAuth } from '../AuthProvider';
import { usePlanLimits } from '../hooks/usePlanLimits';
import { useNavigate } from 'react-router-dom';
import { 
  FileText, 
  Download, 
  Calendar, 
  Filter,
  BarChart3,
  Clock,
  Globe,
  Smartphone,
  RefreshCw,
  Mail,
  ExternalLink,
  BookOpen,
  Megaphone,
  User,
  ShoppingCart
} from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'

interface EmailCapture {
  id: string
  email: string
  captured_at: string
  qr_link_id: string
  qr_title: string
  qr_slug: string
  content_type: string
  book_id?: string
  campaign_id?: string
  book_title?: string
  campaign_title?: string
  author_name?: string
  metadata?: any
}

interface ReportData {
  totalScans: number
  totalLinks: number
  totalCountries: number
  mobileRate: number
  averageTime: string
  topLinks: Array<{
    slug: string
    title: string
    scans: number
    percentage: number
  }>
  countries: Array<{
    country: string
    scans: number
    percentage: number
    flag: string
  }>
  detailedLinks: Array<{
    slug: string
    title: string
    total: number
    today: number
    week: number
    trend: string
    conversion: string
  }>
  emailCaptures: EmailCapture[]
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('30d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ReportData>({
    totalScans: 0,
    totalLinks: 0,
    totalCountries: 0,
    mobileRate: 0,
    averageTime: '--:--',
    topLinks: [],
    countries: [],
    detailedLinks: [],
    emailCaptures: []
  });

  const { user, session } = useAuth();
  const { canAccessReports, loading: planLoading } = usePlanLimits();
  const navigate = useNavigate();

  // Load report data from backend
  const loadReportData = async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      console.log('📊 Loading report data...');

      // Load report data from new API endpoint
      const response = await fetch(`${API_BASE}/api/reports/data?range=${dateRange}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Backend-Fehler: ${response.status}`);
      }

      const data = await response.json();
      
      // Load email captures separately
      const emailResponse = await fetch(`${API_BASE}/api/reports/email-captures`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (emailResponse.ok) {
        const emailData = await emailResponse.json();
        setReportData({
          ...data,
          emailCaptures: emailData.captures || []
        });
      } else {
        setReportData({
          ...data,
          emailCaptures: []
        });
      }

      console.log('✅ Report data loaded');
    } catch (err) {
      console.error('❌ Failed to load report data:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Berichtsdaten');
    } finally {
      setLoading(false);
    }
  };

  // Export emails function
  const handleExportEmails = async (format: 'csv' | 'excel') => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/reports/export-emails/${format}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Export-Fehler: ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `email-export.${format}`;

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(`❌ Failed to export emails as ${format}:`, err);
      setError(err instanceof Error ? err.message : `Fehler beim Export der E-Mails als ${format.toUpperCase()}`);
    }
  };

  // Export functions
  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    if (!session?.access_token) return;

    try {
      const response = await fetch(`${API_BASE}/api/reports/export/${format}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reportType: 'complete',
          dateRange
        })
      });

      if (!response.ok) {
        throw new Error(`Export-Fehler: ${response.status}`);
      }

      // Get filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `report.${format}`;

      // Download the file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error(`❌ Failed to export ${format}:`, err);
      setError(err instanceof Error ? err.message : `Fehler beim Export als ${format.toUpperCase()}`);
    }
  };

  // Check plan access - only after loading is complete
  useEffect(() => {
    if (!planLoading && !canAccessReports()) {
              console.log('🔍 Reports: Redirecting to pricing - no access after loading complete');
        navigate('/qr/pricing');
      return;
    }
  }, [planLoading, canAccessReports, navigate]);

  useEffect(() => {
    loadReportData();
  }, [session, dateRange]);

  // Show loading while plan data is being fetched
  if (planLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Lade Berechtigungen...</span>
      </div>
    );
  }

  // Don't render if no access (after loading)
  if (!planLoading && !canAccessReports()) {
    return null;
  }

  const reportTabs = [
    { id: 'overview', name: 'Übersicht', icon: BarChart3 },
    { id: 'detailed', name: 'Detailliert', icon: FileText },
    { id: 'emails', name: 'E-Mail Adressen', icon: Mail },
    { id: 'export', name: 'Export', icon: Download }
  ];

  const OverviewReport = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <BarChart3 className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Gesamt Scans</p>
              <p className="text-2xl font-bold text-gray-900">{reportData.totalScans.toLocaleString()}</p>
              <p className="text-sm text-green-600">
                {reportData.totalScans > 0 ? '+12% vs. Vormonat' : 'Erstellen Sie QR-Codes'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Globe className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">QR-Codes</p>
              <p className="text-2xl font-bold text-gray-900">{reportData.totalLinks}</p>
              <p className="text-sm text-green-600">
                {reportData.totalLinks > 0 ? 'Aktiv' : 'Keine erstellt'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Smartphone className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Mobile Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {reportData.totalScans > 0 ? `${reportData.mobileRate}%` : '-%'}
              </p>
              <p className="text-sm text-gray-500">
                {reportData.totalScans > 0 ? 'Geschätzt' : 'Keine Daten'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-orange-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ø Tageszeit</p>
              <p className="text-2xl font-bold text-gray-900">{reportData.averageTime}</p>
              <p className="text-sm text-gray-500">
                {reportData.totalScans > 0 ? 'Peak-Zeit' : 'Keine Daten'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Top QR-Codes</h3>
          </div>
          <div className="p-6">
            {reportData.topLinks.length > 0 ? (
              <div className="space-y-4">
                {reportData.topLinks.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">/{item.slug}</span>
                        <span className="text-sm text-gray-500">{item.scans} ({item.percentage}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full bg-blue-500"
                          style={{ width: `${Math.min(item.percentage * 5, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <BarChart3 className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">Keine QR-Codes vorhanden</p>
                <p className="text-xs mt-1">Erstellen Sie QR-Codes um Statistiken zu sehen</p>
              </div>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Geografische Verteilung</h3>
          </div>
          <div className="p-6">
            {reportData.countries.length > 0 ? (
              <div className="space-y-4">
                {reportData.countries.map((item, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center">
                      <span className="text-lg mr-3">{item.flag}</span>
                      <span className="text-sm font-medium text-gray-700">{item.country}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-900">{item.scans}</div>
                      <div className="text-xs text-gray-500">{item.percentage}%</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Globe className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className="text-sm">Keine geografischen Daten</p>
                <p className="text-xs mt-1">Daten werden nach dem ersten Scan verfügbar</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const DetailedReport = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Detaillierte Scan-Analyse</h3>
      </div>
      <div className="overflow-x-auto">
        {reportData.detailedLinks.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  QR-Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Scans Gesamt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Heute (Est.)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Diese Woche (Est.)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trend
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Performance
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {reportData.detailedLinks.map((item, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">/{item.slug}</div>
                      <div className="text-xs text-gray-500">{item.title}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.total}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.today}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{item.week}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`text-sm font-medium ${
                      item.trend.startsWith('+') ? 'text-green-600' : 'text-gray-600'
                    }`}>
                      {item.trend}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.total > 50 ? 'Hoch' : item.total > 20 ? 'Mittel' : item.total > 0 ? 'Niedrig' : 'Keine'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12 text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine detaillierten Daten</h3>
            <p className="text-gray-600">Erstellen Sie QR-Codes um detaillierte Analysen zu sehen.</p>
          </div>
        )}
      </div>
    </div>
  );

  const ExportReport = () => (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Berichte exportieren</h3>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Export-Optionen</h4>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Zeitraum
              </label>
              <select
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="7d">Letzte 7 Tage</option>
                <option value="30d">Letzte 30 Tage</option>
                <option value="90d">Letzte 90 Tage</option>
                <option value="1y">Letztes Jahr</option>
                <option value="all">Gesamter Zeitraum</option>
              </select>
            </div>

            <div className="space-y-3 pt-4">
              <button 
                onClick={() => handleExport('pdf')}
                disabled={reportData.totalLinks === 0}
                className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Als PDF exportieren
              </button>
              
              <button 
                onClick={() => handleExport('excel')}
                disabled={reportData.totalLinks === 0}
                className="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Als Excel exportieren
              </button>
              
              <button 
                onClick={() => handleExport('csv')}
                disabled={reportData.totalLinks === 0}
                className="w-full bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Als CSV exportieren
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-md font-medium text-gray-900">Vorschau</h4>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="text-sm text-gray-600 space-y-2">
                <div>📊 <strong>Report-Typ:</strong> Vollständiger Report</div>
                <div>📅 <strong>Zeitraum:</strong> {
                  dateRange === '7d' ? 'Letzte 7 Tage' :
                  dateRange === '30d' ? 'Letzte 30 Tage' :
                  dateRange === '90d' ? 'Letzte 90 Tage' :
                  dateRange === '1y' ? 'Letztes Jahr' : 'Gesamter Zeitraum'
                }</div>
                <div>📈 <strong>QR-Codes:</strong> {reportData.totalLinks} Einträge</div>
                <div>📊 <strong>Gesamt-Scans:</strong> {reportData.totalScans.toLocaleString()}</div>
                <div>📄 <strong>Geschätzte Größe:</strong> {reportData.totalLinks > 0 ? `${Math.max(1, Math.floor(reportData.totalLinks / 10))} MB` : '< 1 KB'}</div>
              </div>
            </div>

            {reportData.totalLinks === 0 && (
              <div className="bg-blue-50 rounded-lg p-4">
                <h5 className="text-sm font-medium text-blue-900 mb-2">ℹ️ Hinweis</h5>
                <p className="text-sm text-blue-700">
                  Erstellen Sie zunächst QR-Codes um Berichte generieren zu können.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const EmailsReport = () => {
    // Group emails by content type
    const emailsByType = reportData.emailCaptures.reduce((acc, capture) => {
      const type = capture.content_type || 'unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(capture);
      return acc;
    }, {} as Record<string, EmailCapture[]>);

    const getContentTypeLabel = (type: string) => {
      switch (type) {
        case 'author': return 'Autorenprofil';
        case 'sample': return 'Leseprobe';
        case 'multishop': return 'Multi-Shop';
        default: return type;
      }
    };

    const getContentTypeIcon = (type: string) => {
      switch (type) {
        case 'author': return <User className="w-4 h-4 text-blue-500" />;
        case 'sample': return <FileText className="w-4 h-4 text-green-500" />;
        case 'multishop': return <ShoppingCart className="w-4 h-4 text-purple-500" />;
        default: return <Mail className="w-4 h-4 text-gray-500" />;
      }
    };

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Mail className="h-5 w-5 mr-2" />
              Erfasste E-Mail Adressen
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => handleExportEmails('csv')}
                disabled={reportData.emailCaptures.length === 0}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                CSV Export
              </button>
              <button
                onClick={() => handleExportEmails('excel')}
                disabled={reportData.emailCaptures.length === 0}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="h-4 w-4 mr-2" />
                Excel Export
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Gesamt E-Mails</p>
              <p className="text-2xl font-bold text-gray-900">{reportData.emailCaptures.length}</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Einzigartige E-Mails</p>
              <p className="text-2xl font-bold text-gray-900">
                {new Set(reportData.emailCaptures.map(c => c.email)).size}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Content-Typen</p>
              <p className="text-2xl font-bold text-gray-900">{Object.keys(emailsByType).length}</p>
            </div>
          </div>

          {reportData.emailCaptures.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Mail className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p>Noch keine E-Mail Adressen erfasst</p>
              <p className="text-sm mt-1">E-Mails werden bei Leseproben und Autorenprofilen mit aktiviertem E-Mail Gate erfasst.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      E-Mail Adresse
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Content-Typ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      QR-Code / Titel
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Buch/Kampagne
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Erfasst am
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.emailCaptures.map((capture) => (
                    <tr key={capture.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {capture.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          {getContentTypeIcon(capture.content_type)}
                          {getContentTypeLabel(capture.content_type)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {capture.qr_title}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {capture.book_title ? (
                          <div className="flex items-center gap-1">
                            <BookOpen className="w-4 h-4" />
                            {capture.book_title}
                          </div>
                        ) : capture.campaign_title ? (
                          <div className="flex items-center gap-1">
                            <Megaphone className="w-4 h-4" />
                            {capture.campaign_title}
                          </div>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(capture.captured_at).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => navigate(`/qr/dash/links?edit=${capture.qr_link_id}`)}
                          className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                          title="QR-Code anzeigen"
                        >
                          <ExternalLink className="w-4 h-4" />
                          QR-Code
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!user) {
    return <div>Nicht angemeldet</div>;
  }

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Berichte</h1>
          <p className="text-gray-600">Detaillierte Auswertungen und Export-Funktionen</p>
        </div>
        
        <div className="flex space-x-2">
          <button
            onClick={loadReportData}
            disabled={loading}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
          
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="7d">7 Tage</option>
            <option value="30d">30 Tage</option>
            <option value="90d">90 Tage</option>
          </select>
          
          <button 
            onClick={() => handleExport('pdf')}
            disabled={reportData.totalLinks === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="h-4 w-4 mr-2" />
            Schnell-Export
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-100 border border-red-300 rounded-lg text-red-700">
          ❌ {error}
          <button 
            onClick={loadReportData}
            className="ml-4 text-sm underline hover:no-underline"
          >
            Erneut versuchen
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Berichtsdaten werden geladen...</p>
        </div>
      )}

      {/* Tabs */}
      {!loading && (
        <>
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {reportTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {tab.name}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'overview' && <OverviewReport />}
            {activeTab === 'detailed' && <DetailedReport />}
            {activeTab === 'emails' && <EmailsReport />}
            {activeTab === 'export' && <ExportReport />}
          </div>
        </>
      )}
      </div>
    </div>
  );
} 